import { useCallback, useRef, useState } from 'react';
import useSWR from 'swr';

import { backendClient } from '@/lib/api-client';
import {
  type CalendarSyncPreferences,
  DEFAULT_CALENDAR_SYNC_PREFERENCES,
} from '@/lib/calendarSyncPreferences';

const CALENDAR_SYNC_PREFERENCES_KEY = '/user/me/calendar-sync-preferences';

export const useCalendarSyncPreferences = () => {
  const { data, error, isLoading, mutate } = useSWR<CalendarSyncPreferences>(
    CALENDAR_SYNC_PREFERENCES_KEY,
    async (url) => {
      const response = await backendClient.get(url);
      return response.data as CalendarSyncPreferences;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    },
  );

  const [isSaving, setIsSaving] = useState(false);
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingSavesRef = useRef(0);
  const latestSaveIdRef = useRef(0);

  const save = useCallback(
    (patch: Partial<CalendarSyncPreferences>) => {
      const saveId = ++latestSaveIdRef.current;
      pendingSavesRef.current += 1;
      setIsSaving(true);

      const run = async () => {
        const response = await backendClient.patch(CALENDAR_SYNC_PREFERENCES_KEY, patch);
        const updated = response.data as CalendarSyncPreferences;
        if (saveId === latestSaveIdRef.current) {
          await mutate(updated, { revalidate: false });
        }
        return updated;
      };

      const result = saveChainRef.current.then(run, run);
      saveChainRef.current = result.then(
        () => undefined,
        () => undefined,
      );
      void saveChainRef.current.finally(() => {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) setIsSaving(false);
      });
      return result;
    },
    [mutate],
  );

  const refetch = useCallback(async () => {
    try {
      const result = await mutate();
      return { data: result, error: undefined };
    } catch (err) {
      return { data: undefined, error: err };
    }
  }, [mutate]);

  return {
    data: data ?? DEFAULT_CALENDAR_SYNC_PREFERENCES,
    isLoading,
    isError: Boolean(error),
    error,
    refetch,
    save,
    isSaving,
  };
};
